import { iframeDataBridge } from '@/iframe/iframeDataBridge';

// Reads the legacy agenda (vmAgenda datasource, DELIVERYLOCATIONGEO, Legislation/Routing modules) and turns
// it into the plain day problems routeOptimizer.js expects (#23500). Read-only: nothing here writes back.

export const toDayKey = (date) => {
	const d = new Date(date);
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const startOfDay = (date) => {
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	return d;
};

// Calendar day step, not +24 h: the DST change days last 23 h / 25 h.
const nextDay = (day) => new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);

// Wall-clock time of the day (setHours), not elapsed time since midnight: right on the DST change days too.
export const atHour = (day, hours) => {
	const minutes = Math.round(hours * 60);
	const d = new Date(day);
	d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
	return d.getTime();
};

function readLegislation(sfaHelper, name) {
	try {
		const value = Number(sfaHelper?.getLegislation?.(name));
		return Number.isFinite(value) ? value : null;
	} catch {
		return null;
	}
}

// Same rule as the agenda (agenda.js init): an empty or 0 value falls back to 8 h.
export function readStartHourAM(salesHelper, sfaHelper) {
	const settings = salesHelper?.getModule?.(sfaHelper?.getApplicationDomainIdSFA?.(), 'Legislation')?.settings || [];
	return Number(settings.find((s) => s.name === 'StartHourAM')?.value) || 8;
}

// Same rule as cartFormMerch.getHourSubdivision (FIX#44955): 'Subdivision' module, Hour = 15 | 30 | Full, 15 min when not set.
export function readHourSubdivision(salesHelper, sfaHelper) {
	const settings = salesHelper?.getModule?.(sfaHelper?.getApplicationDomainIdSFA?.(), 'Subdivision')?.settings || [];
	const value = String(settings.find((s) => s.name === 'Hour')?.value ?? '')
		.trim()
		.toLowerCase();
	return value === 'full' ? 60 : value === '30' ? 30 : 15;
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const mondayFirst = (day) => (day + 6) % 7;

/**
 * Days the agenda lets the user plan visits on (0 = Sunday … 6 = Saturday, Monday first), same rule as the
 * agenda businessHours (agenda.js init): SALFO options WorkingDays / BusinessDays / ExcludedDays, each one
 * only when sfaHelper.getSFAAuthorizedDaysType() allows it. null when nothing is configured.
 */
export function parseWorkingWeekdays(options, authorized) {
	const valueOf = (name) => options?.find((o) => o.name === name && o.application === 'SALFO')?.value;
	const parts = [authorized?.workingDays && valueOf('WorkingDays'), authorized?.businessDays && valueOf('BusinessDays'), authorized?.excludedDays && valueOf('ExcludedDays')].filter((v) => typeof v === 'string');
	const days = new Set(
		parts
			.join(',')
			.split(',')
			.map((d) => d.trim())
			.filter(Boolean)
			.map((d) => (WEEKDAY_NAMES.includes(d) ? WEEKDAY_NAMES.indexOf(d) : Number(d)))
			.filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
	);
	return days.size ? [...days].sort((a, b) => mondayFirst(a) - mondayFirst(b)) : null;
}

export function readWorkingWeekdays() {
	try {
		return parseWorkingWeekdays(iframeDataBridge.getIframeWindow()?.externis?.ecosVM?.options, iframeDataBridge.getSfaHelper()?.getSFAAuthorizedDaysType?.());
	} catch {
		return null;
	}
}

/**
 * Days of [start, end) shown in the week preview: the working days, plus any other day that already holds
 * appointments (they are still optimized, a visit never changes day). Keys are 'YYYY-MM-DD'.
 */
export function displayedDayKeys(start, end, workingWeekdays, busyDayKeys = new Set()) {
	const allowed = new Set(workingWeekdays ?? [1, 2, 3, 4, 5]);
	const keys = [];
	for (let day = startOfDay(start); day < new Date(end); day = nextDay(day)) {
		const key = toDayKey(day);
		if (allowed.has(day.getDay()) || busyDayKeys.has(key)) keys.push(key);
	}
	return keys;
}

const formatAddress = (dl) => [dl?.addressLine, dl?.city].filter(Boolean).join(' · ');

/**
 * Builds the optimization input for every day of [start, end). Past days and today are flagged `excluded`:
 * only the upcoming days are optimized, counted in the total and drawn on the map.
 *
 * @param {object} scope { start, end, viewType }  dates sent by vwSFAPlanning.optimizeRoute()
 * @param {Set<string>} pinnedUids  pins from AppointmentStore
 * @returns {Promise<{days: object[], points: object[], coordinates: number[][], stepMinutes: number}>}
 */
export async function buildRouteProblems(scope, pinnedUids = new Set()) {
	const iframeWin = iframeDataBridge.getIframeWindow();
	const externis = iframeWin?.externis;
	const vmAgenda = externis?.sales?.vmAgenda;
	const sfaHelper = iframeDataBridge.getSfaHelper();
	const salesHelper = iframeDataBridge.getSalesHelper();
	const enumAppointment = iframeDataBridge.enumAppointment;
	const user = externis?.ctx?.user;

	const deliveryLocations = externis?.ecosVM?.deliveryLocations || [];
	const geoInfos = (await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONGEO')) || [];
	const geoById = new Map(geoInfos.filter((g) => g?.latitude && g?.longitude).map((g) => [g.deliveryLocationId, g]));
	const dlById = new Map(deliveryLocations.map((dl) => [dl.id, dl]));

	const nightSubType = sfaHelper?.appointment?.getDefaultSubtypeNight?.() ?? enumAppointment.getSubTypeId('NIGHT');
	const externalSubType = sfaHelper?.appointment?.getDefaultSubtypeExternal?.();
	const routing = sfaHelper?.getRouting?.() || { enabled: false };
	const withFirstLeg = routing.enabled ? routing.enableFirstRoute : true;
	const withLastLeg = routing.enabled ? routing.enableLastRoute : true;

	const startHourAM = readStartHourAM(salesHelper, sfaHelper);
	const maxWorkHours = readLegislation(sfaHelper, 'MaxConsecutiveWorkDuration');
	const maxWorkMinutes = maxWorkHours && maxWorkHours < 24 ? maxWorkHours * 60 : null;
	const stepMinutes = readHourSubdivision(salesHelper, sfaHelper);

	const points = [];
	const pointIndex = new Map();
	const pointOf = (dlId, kind) => {
		const geo = geoById.get(dlId);
		if (!dlId || !geo) return null;
		if (!pointIndex.has(dlId)) {
			const dl = dlById.get(dlId);
			pointIndex.set(dlId, points.length);
			points.push({ deliveryLocationId: dlId, kind, label: dl?.companyName || dl?.label || '', address: formatAddress(dl), lat: geo.latitude, lng: geo.longitude });
		}
		return pointIndex.get(dlId);
	};

	// External calendar events (Outlook synchronisation) are ignored altogether: neither reordered nor anchors.
	const isExternal = (a) => a.type === enumAppointment.type.HC && (a.subType === externalSubType || !!a.externalId);
	const appointments = (vmAgenda?.datasource || []).filter((a) => a && a.status !== enumAppointment.status.CANCELLED && !isExternal(a));
	const now = Date.now();
	const todayKey = toDayKey(now);
	const homeId = user?.deliveryLocationHomeId || null;

	const days = [];
	for (let day = startOfDay(scope.start); day < new Date(scope.end); day = nextDay(day)) {
		const dayStart = day.getTime();
		const dayEnd = nextDay(day).getTime();
		const nights = appointments.filter((a) => a.subType === nightSubType);
		const nightBefore = nights.find((a) => new Date(a.endDate).getTime() > dayStart && new Date(a.startDate).getTime() < dayStart);
		const nightAfter = nights.find((a) => new Date(a.startDate).getTime() >= dayStart && new Date(a.startDate).getTime() < dayEnd);

		const items = appointments
			.filter((a) => a.subType !== nightSubType)
			.filter((a) => {
				const start = new Date(a.startDate).getTime();
				return start >= dayStart && start < dayEnd;
			})
			.map((a) => {
				const isStore = a.type === enumAppointment.type.CL;
				const dl = dlById.get(a.deliveryLocationId);
				// A visit already started is never rescheduled, even with the "past visit" permission canChangeAppointmentStartHour honours.
				let role = 'hc';
				if (isStore && pinnedUids.has(a.uid)) role = 'pinned';
				else if (isStore && (new Date(a.startDate).getTime() < now || !sfaHelper?.canChangeAppointmentStartHour?.(a) || !sfaHelper?.canChangeAppointmentEndHour?.(a))) role = 'locked';
				else if (isStore) role = 'visit';
				return {
					uid: a.uid,
					deliveryLocationId: a.deliveryLocationId || null,
					label: isStore ? dl?.companyName || sfaHelper?.appointment?.getDisplayLabel?.(a) || a.label || '' : sfaHelper?.appointment?.getDisplayLabel?.(a) || a.label || '',
					address: isStore ? formatAddress(dl) : '',
					start: new Date(a.startDate).getTime(),
					end: new Date(a.endDate).getTime(),
					point: isStore ? pointOf(a.deliveryLocationId, 'store') : null,
					isStore,
					role,
					color: sfaHelper?.getAppointmentColor?.(a) || null,
					bgColor: sfaHelper?.getAppointmentBGColor?.(a) || null,
				};
			});

		const startNightPoint = nightBefore ? pointOf(nightBefore.deliveryLocationId, 'night') : null;
		const homePoint = pointOf(homeId, 'home');
		const startPoint = withFirstLeg ? (startNightPoint ?? homePoint) : null;
		const endNightPoint = nightAfter ? pointOf(nightAfter.deliveryLocationId, 'night') : null;
		const endPoint = withLastLeg ? (endNightPoint ?? homePoint) : null;

		days.push({
			date: toDayKey(day),
			excluded: toDayKey(day) <= todayKey,
			items,
			startPoint,
			endPoint,
			startKind: startPoint === null ? 'first' : startPoint === startNightPoint ? 'night' : 'home',
			endKind: endPoint === null ? null : endPoint === endNightPoint ? 'night' : 'home',
			bounds: { earliestStart: atHour(day, startHourAM), maxWorkMinutes, dayEnd: atHour(day, 23.75) },
		});
	}

	return { days, points, coordinates: points.map((p) => [p.lng, p.lat]), stepMinutes };
}
